/**
 * Users page (`/users`) — Admin only.
 *
 * Lists every user with role, status, last login.
 * Admin actions:
 *   - Create new user (sets initial password)
 *   - Edit display name, email, role, isActive
 *   - Reset password (sets a new one; existing sessions stay valid — by design)
 *
 * The current user cannot deactivate or demote themselves — UI gates this and
 * the server enforces it too.
 */
import { useEffect, useState } from 'react';
import { Edit3, KeyRound, Plus } from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';
import { formatRelative } from '@procurement/shared';
import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { DataTable, type ColumnDef } from '@/components/ui/data-table';
import { EmptyState } from '@/components/ui/empty-state';
import { FormField, Input } from '@/components/ui/form-field';
import { Modal } from '@/components/ui/modal';
import { PageHeader } from '@/components/ui/page-header';
import { Select } from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { useCurrentUser } from '@/lib/auth';
import { usePageTitle } from '@/lib/use-page-title';
import {
  useCreateUser,
  useResetPassword,
  useUpdateUser,
  useUsersList,
  type UserRecord,
  type UserRole,
} from '@/lib/users-audit-queries';

export function UsersPage() {
  usePageTitle('Users');
  const me = useCurrentUser();
  const isAdmin = me.data?.user.role === 'ADMIN';
  const navigate = useNavigate();
  const list = useUsersList();
  const [editUser, setEditUser] = useState<UserRecord | null>(null);
  const [resetPasswordFor, setResetPasswordFor] = useState<UserRecord | null>(null);
  const [showNew, setShowNew] = useState(false);

  // Defensive redirect — Admin-only view
  useEffect(() => {
    if (me.data && !isAdmin) void navigate({ to: '/', replace: true });
  }, [isAdmin, me.data, navigate]);

  if (!isAdmin) return null;

  if (list.isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner className="h-6 w-6 text-secondary" />
      </div>
    );
  }

  const rows = list.data ?? [];
  const myId = me.data?.user.id;

  const columns: ColumnDef<UserRecord>[] = [
    {
      id: 'displayName',
      header: 'User',
      cell: (r) => (
        <div>
          <div className="font-medium text-primary">{r.displayName}</div>
          <div className="font-mono text-xs text-secondary">@{r.username}</div>
        </div>
      ),
    },
    {
      id: 'email',
      header: 'Email',
      cell: (r) => <span className="text-secondary">{r.email}</span>,
    },
    {
      id: 'role',
      header: 'Role',
      width: '110px',
      cell: (r) =>
        r.role === 'ADMIN' ? <Badge tone="info">Admin</Badge> : <Badge>Assistant</Badge>,
    },
    {
      id: 'status',
      header: 'Status',
      width: '110px',
      cell: (r) =>
        r.isActive ? (
          <Badge tone="success">Active</Badge>
        ) : (
          <Badge tone="neutral">Disabled</Badge>
        ),
    },
    {
      id: 'lastLogin',
      header: 'Last login',
      width: '130px',
      cell: (r) => (
        <span className="text-xs text-secondary">
          {r.lastLoginAt ? formatRelative(r.lastLoginAt) : 'Never'}
        </span>
      ),
    },
    {
      id: 'actions',
      header: '',
      width: '120px',
      cell: (r) => {
        return (
          <div className="flex items-center justify-end gap-1">
            <button
              type="button"
              aria-label="Edit user"
              onClick={() => setEditUser(r)}
              className="rounded-sm p-1 text-tertiary hover:bg-subtle hover:text-primary"
            >
              <Edit3 className="h-3.5 w-3.5" strokeWidth={1.5} />
            </button>
            <button
              type="button"
              aria-label="Reset password"
              title="Reset password"
              onClick={() => setResetPasswordFor(r)}
              className="rounded-sm p-1 text-tertiary hover:bg-subtle hover:text-primary"
            >
              <KeyRound className="h-3.5 w-3.5" strokeWidth={1.5} />
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <PageHeader
        title="Users"
        subtitle="Manage who can access the system."
        actions={
          <Button
            variant="primary"
            size="default"
            leftIcon={<Plus className="h-4 w-4" />}
            onClick={() => setShowNew(true)}
          >
            New user
          </Button>
        }
      />

      <Card bodyClassName="p-0">
        {rows.length === 0 ? (
          <EmptyState
            title="No users yet"
            body="Add the first administrator to get started."
            action={
              <Button onClick={() => setShowNew(true)}>
                <Plus className="h-3.5 w-3.5" strokeWidth={1.5} />
                Add user
              </Button>
            }
          />
        ) : (
          <DataTable<UserRecord> columns={columns} rows={rows} rowKey={(r) => r.id} />
        )}
      </Card>

      {showNew ? <UserCreateModal onClose={() => setShowNew(false)} /> : null}
      {editUser ? (
        <UserEditModal
          user={editUser}
          isSelf={editUser.id === myId}
          onClose={() => setEditUser(null)}
        />
      ) : null}
      {resetPasswordFor ? (
        <ResetPasswordModal user={resetPasswordFor} onClose={() => setResetPasswordFor(null)} />
      ) : null}
    </>
  );
}

// ---------------------------------------------------------------------------
// Create user
// ---------------------------------------------------------------------------

function UserCreateModal({ onClose }: { onClose: () => void }) {
  const create = useCreateUser();
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<UserRole>('ASSISTANT');
  const [initialPassword, setInitialPassword] = useState('');

  const required =
    username.trim().length > 0 &&
    displayName.trim().length > 0 &&
    email.trim().length >= 3 &&
    initialPassword.length >= 12;

  const submit = (): void => {
    if (!required) return;
    create.mutate(
      {
        username: username.trim(),
        displayName: displayName.trim(),
        email: email.trim(),
        role,
        initialPassword,
      },
      { onSuccess: onClose },
    );
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="New user"
      description="The user signs in with the initial password you set below. They can rotate it from their profile after first login."
      footer={
        <>
          <Button variant="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={submit}
            disabled={!required}
            loading={create.isPending}
          >
            Create user
          </Button>
        </>
      }
    >
      {create.error ? (
        <Alert tone="danger" className="mb-3">
          {create.error.message}
        </Alert>
      ) : null}
      <FormField label="Username" required helpText="Lowercase, no spaces. Used to sign in.">
        <Input autoFocus value={username} onChange={(e) => setUsername(e.target.value)} />
      </FormField>
      <FormField label="Display name" required>
        <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
      </FormField>
      <FormField label="Email" required>
        <Input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="name@example.com"
        />
      </FormField>
      <FormField label="Role" required>
        <Select value={role} onChange={(e) => setRole(e.target.value as UserRole)}>
          <option value="ASSISTANT">Assistant — operational access</option>
          <option value="ADMIN">Administrator — unrestricted access</option>
        </Select>
      </FormField>
      <FormField
        label="Initial password"
        required
        helpText="Minimum 12 characters. The user can change this from their profile."
      >
        <Input
          type="password"
          value={initialPassword}
          onChange={(e) => setInitialPassword(e.target.value)}
          autoComplete="new-password"
        />
      </FormField>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Edit user
// ---------------------------------------------------------------------------

function UserEditModal({
  user,
  isSelf,
  onClose,
}: {
  user: UserRecord;
  isSelf: boolean;
  onClose: () => void;
}) {
  const update = useUpdateUser();
  const [displayName, setDisplayName] = useState(user.displayName);
  const [email, setEmail] = useState(user.email);
  const [role, setRole] = useState<UserRole>(user.role);
  const [isActive, setIsActive] = useState(user.isActive);

  const submit = (): void => {
    update.mutate(
      {
        id: user.id,
        displayName: displayName.trim(),
        email: email.trim(),
        role,
        isActive,
        rowVersion: user.rowVersion,
      },
      { onSuccess: onClose },
    );
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={`Edit @${user.username}`}
      description={
        isSelf
          ? 'Editing your own account. You cannot demote yourself or disable your account.'
          : undefined
      }
      footer={
        <>
          <Button variant="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submit} loading={update.isPending}>
            Save changes
          </Button>
        </>
      }
    >
      {update.error ? (
        <Alert tone="danger" className="mb-3">
          {update.error.message}
        </Alert>
      ) : null}
      {isSelf ? (
        <Alert tone="info" className="mb-3">
          Use a separate admin account to manage other users; safety actions on your own account are
          disabled.
        </Alert>
      ) : null}
      <FormField label="Display name" required>
        <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
      </FormField>
      <FormField label="Email" required>
        <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
      </FormField>
      <FormField label="Role" required>
        <Select
          value={role}
          onChange={(e) => setRole(e.target.value as UserRole)}
          disabled={isSelf}
        >
          <option value="ASSISTANT">Assistant</option>
          <option value="ADMIN">Administrator</option>
        </Select>
      </FormField>
      <FormField label="Account status">
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="active"
            checked={isActive}
            disabled={isSelf}
            onChange={(e) => setIsActive(e.target.checked)}
            className="h-4 w-4 rounded border-border-default text-accent focus:ring-accent"
          />
          <label htmlFor="active" className="text-sm text-primary">
            Account is active
          </label>
        </div>
      </FormField>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Reset password
// ---------------------------------------------------------------------------

function ResetPasswordModal({ user, onClose }: { user: UserRecord; onClose: () => void }) {
  const reset = useResetPassword();
  const [pw, setPw] = useState('');

  const submit = (): void => {
    if (pw.length < 12) return;
    reset.mutate({ id: user.id, newPassword: pw }, { onSuccess: onClose });
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={`Reset password for @${user.username}`}
      description="Existing sessions stay valid. Share the new password securely; the user can change it at any time."
      size="sm"
      footer={
        <>
          <Button variant="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={submit}
            disabled={pw.length < 12}
            loading={reset.isPending}
          >
            Reset password
          </Button>
        </>
      }
    >
      {reset.error ? (
        <Alert tone="danger" className="mb-3">
          {reset.error.message}
        </Alert>
      ) : null}
      <FormField label="New password" required helpText="Minimum 12 characters.">
        <Input
          autoFocus
          type="password"
          value={pw}
          onChange={(e) => setPw(e.target.value)}
          autoComplete="new-password"
        />
      </FormField>
    </Modal>
  );
}
